import dayjs from "apps/visits/packages/shared/libs/dayjs";
import type { AxiosError } from "axios";
import { useState } from "react";
import type { SubmitErrorHandler, SubmitHandler, UseFormMethods } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { extractDateOfBirthFromPesel } from "@/core/utils/adulthood";
import type {
  PeselOrDateOfBirth,
  VerifyPhoneNumberErrorResponse,
  VerifyPhoneNumberResponse,
} from "../../api/group-actions-ordering.types";
import { useVerifyPhoneNumber } from "../../api/verify-phone-number.mutation";
import { DATE_FORMAT } from "../../config/date-time";
import { useRoutes } from "../../hooks/use-routes";
import { useOrderingStore } from "../../store/use-ordering-store";
import { addPatientErrorEvent, addPatientEvent, safeTrackEvent } from "../../tracking/events";
import type { AgreementStates } from "../../types";
import { buildAgreementsPayload } from "../../utils/build-agreements-payload";
import { guessGenderFromFirstName } from "../../utils/guess-gender-from-first-name";
import type { PhoneVerificationFormValues } from "./index";

type UsePhoneVerificationLogicProps = {
  formMethods: UseFormMethods<PhoneVerificationFormValues>;
  agreementStates: AgreementStates;
};

export const usePhoneVerificationLogic = ({ formMethods, agreementStates }: UsePhoneVerificationLogicProps) => {
  const { goToRegistration, goToLogin, goToPhoneVerificationError } = useRoutes();
  const [generalError, setGeneralError] = useState();
  const { t } = useTranslation();
  const {
    setPesel,
    setPhoneInfo,
    setDateOfBirth,
    setFullName,
    setEmail,
    setAgreements,
    setVerificationType,
    setVerificationTokenUuid,
  } = useOrderingStore();
  const { mutate: verifyPhoneNumber, isLoading } = useVerifyPhoneNumber({});

  const formatDateOfBirth = (dateOfBirth: Date) => {
    return dayjs(dateOfBirth).format(DATE_FORMAT);
  };

  const setErrorInFieldForm = (fieldName: string, message: string) => {
    formMethods.setError(fieldName, { type: "custom", message });
  };

  const getPeselOrDateOfBirth = (isDateOfBirth: boolean, dateOfBirth?: Date, pesel?: string): PeselOrDateOfBirth => {
    if (isDateOfBirth && dateOfBirth) {
      return { dateOfBirth: formatDateOfBirth(dateOfBirth) };
    }
    if (pesel) {
      return { pesel };
    }

    throw new Error("Either dateOfBirth or pesel must be provided");
  };

  const handleVerifyPhoneNumberResponse = (response: VerifyPhoneNumberResponse) => {
    setVerificationType(response.verificationType);

    if (response.verificationType === "email") setEmail(response.email);
    else setEmail(undefined);

    if (response.verificationType === "phone" || response.verificationType === "email") {
      setVerificationTokenUuid(response.verificationTokenUuid);
      return goToLogin();
    }

    return goToRegistration();
  };

  const handleVerifyPhoneNumberError = (errorObj: AxiosError<VerifyPhoneNumberErrorResponse, any>) => {
    const apiErrors = errorObj.response?.data.error;
    const alreadyRegisteredError = apiErrors?.base?.filter((baseErr) => baseErr.error === "patient_already_registered");
    const phoneWithDialCodeError = apiErrors?.base?.filter(
      (baseErr) => baseErr.error === "invalid_phone_with_dial_code",
    );

    let errorType = "unknown";
    if (apiErrors?.pesel) errorType = "pesel";
    else if (apiErrors?.phone || (phoneWithDialCodeError && phoneWithDialCodeError.length > 0)) errorType = "phone";
    else if (alreadyRegisteredError && alreadyRegisteredError.length > 0) errorType = "patient_already_registered";

    safeTrackEvent(
      () => addPatientErrorEvent({ errorType }),
      "add_patient_error",
      "phone-verification verifyPhoneNumber.onError",
    );

    if (apiErrors?.pesel) {
      setErrorInFieldForm("pesel", t("ERRORS.COMMONS.INCORRECT_PESEL"));
    } else if (apiErrors?.phone || (phoneWithDialCodeError && phoneWithDialCodeError.length > 0)) {
      setErrorInFieldForm("phone", t("ERRORS.COMMONS.INCORRECT_PHONE_NUMBER"));
    } else if (alreadyRegisteredError && alreadyRegisteredError.length > 0) {
      setGeneralError(t("GROUP_ACTIONS_ORDERING.ERRORS.PATIENT_ALREADY_REGISTERED"));
    } else {
      goToPhoneVerificationError();
    }
  };

  const setPersonalDetails = (formValues: PhoneVerificationFormValues) => {
    const { isDateOfBirth, dateOfBirth, pesel, dialCode, phone, firstName, lastName } = formValues;

    if (isDateOfBirth && dateOfBirth) {
      setDateOfBirth(formatDateOfBirth(dateOfBirth));
      setPesel(undefined);
    } else {
      setPesel(pesel);
      setDateOfBirth(undefined);
    }
    setPhoneInfo({ dialCode, phone });
    setFullName(firstName, lastName);
    setAgreements(buildAgreementsPayload(formValues, agreementStates));
  };

  const trackAddPatient = (formValues: PhoneVerificationFormValues) => {
    const yearFromPesel = extractDateOfBirthFromPesel(formValues.pesel)?.getFullYear() ?? null;
    const yearFromDate =
      formValues.isDateOfBirth && formValues.dateOfBirth
        ? (() => {
            const d = dayjs(formValues.dateOfBirth);
            return d.isValid() ? d.year() : null;
          })()
        : null;

    // Read consent flags from the filtered payload, not raw form values:
    // hidden channels must report false in analytics regardless of what the
    // shared marketing checkbox forced into RHF state.
    const agreements = buildAgreementsPayload(formValues, agreementStates);

    safeTrackEvent(
      () =>
        addPatientEvent({
          yearOfBirth: yearFromPesel ?? yearFromDate,
          gender: guessGenderFromFirstName(formValues.firstName),
          newsletterConsent: !!agreements.acceptNewsletter,
          phoneCallConsent: !!agreements.acceptMarketingPhoneContact,
        }),
      "add_patient",
      "phone-verification verifyPhoneNumber.onSuccess",
    );
  };

  const handleOnSubmit: SubmitHandler<PhoneVerificationFormValues> = (formValues) => {
    try {
      const dataToSend = {
        dialCode: formValues.dialCode,
        phone: formValues.phone,
        ...getPeselOrDateOfBirth(formValues.isDateOfBirth, formValues.dateOfBirth, formValues.pesel),
      };

      verifyPhoneNumber(dataToSend, {
        onSuccess: (response) => {
          setPersonalDetails(formValues);
          trackAddPatient(formValues);
          handleVerifyPhoneNumberResponse(response);
        },
        onError: handleVerifyPhoneNumberError,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleOnInvalidSubmit: SubmitErrorHandler<PhoneVerificationFormValues> = (errors) => {
    // Keep errorType cardinality bounded: report the first invalid field (sorted for determinism)
    // or fall back to "validation". Joining all keys explodes into combinations like
    // "firstName,lastName,pesel,phone" that are useless for aggregation in GA4.
    const errorType = Object.keys(errors).sort()[0] ?? "validation";
    safeTrackEvent(
      () => addPatientErrorEvent({ errorType }),
      "add_patient_error",
      "phone-verification handleSubmit invalid",
    );
  };

  return {
    handleOnSubmit,
    handleOnInvalidSubmit,
    isLoading,
    generalError,
  };
};
